import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useLazyQuery } from "@apollo/client";
import { GET_CAMPAIGNS } from "graphql/campaign/campaign.query";
import { GET_CAMPAIGNS_BY_ORG_ID } from "graphql/campaign/campaign.query";
import { DELETE_CAMPAIGN } from "graphql/campaign/campaign.mutation";
import { Campaign } from "types/campaign";
import {
  NotificationSeverity,
  TABLE_ACTIONS,
} from "helpers/constants";
import { useNotifications } from "hooks/useNotifications";

export const useCampaign = () => {
  const navigate = useNavigate();
  const [deleteCampaignOpen, setDeleteCampaignOpen] = useState(false);
  const [campaignIdToDelete, setCampaignIdToDelete] = useState("");
  const { organizationId } = useParams();
  const isOrgView = useMemo(() => {
    return !!organizationId;
  }, [organizationId]);

  const [deleteCampaign] = useMutation(DELETE_CAMPAIGN, {
    refetchQueries: organizationId
      ? [
          {
            query: GET_CAMPAIGNS_BY_ORG_ID,
            variables: { orgId: organizationId },
          },
        ]
      : [{ query: GET_CAMPAIGNS }],
  });
  const { notify } = useNotifications();

  const { data, error, loading: campaignsLoading, refetch } = useQuery(GET_CAMPAIGNS);
  const [
    getCampaignsByOrgId,
    { data: orgData, loading: orgLoading, refetch: refetchOrgCampaigns },
  ] = useLazyQuery(GET_CAMPAIGNS_BY_ORG_ID);

  const loading = useMemo(() => {
    return campaignsLoading || orgLoading;
  }, [campaignsLoading, orgLoading]);

  const fetchOrgCampaigns = useCallback(async () => {
    if (organizationId) {
      const result = await getCampaignsByOrgId({
        variables: { orgId: organizationId },
        fetchPolicy: "network-only", // Always fetch fresh data
      });
      return result;
    }
    return null;
  }, [organizationId, getCampaignsByOrgId]);

  useEffect(() => {
    if (organizationId) {
      fetchOrgCampaigns();
    } else {
      // Refetch all campaigns when not in org view
      refetch();
    }
  }, [organizationId, fetchOrgCampaigns, refetch]);


  const campaigns = useMemo(() => {
    if (organizationId) {
      return (orgData?.campaign as Campaign[]) || [];
    }
    // In admin view, only show campaigns without org_id
    const allCampaigns = (data?.campaign as Campaign[]) || [];
    return allCampaigns.filter((campaign) => !campaign.org_id);
  }, [organizationId, orgData, data]);

  const handleActionClick = (action: TABLE_ACTIONS, id: string) => {
    switch (action) {
      case TABLE_ACTIONS.EDIT:
        navigate(
          `${organizationId ? `/organization/${organizationId}` : ""}/campaign/${id}/edit`,
        );
        break;
      case TABLE_ACTIONS.DELETE:
        setCampaignIdToDelete(id);
        setDeleteCampaignOpen(true);
        break;
      default:
        break;
    }
  };

  const handleDelete = async () => {
    if (!campaignIdToDelete || campaignIdToDelete === "") {
      return;
    }
    deleteCampaign({
      variables: { campaign_id: campaignIdToDelete },
    })
      .then(async () => {
        if (organizationId && refetchOrgCampaigns) {
          await refetchOrgCampaigns({ orgId: organizationId });
        } else if (refetch) {
          await refetch();
        }
        setCampaignIdToDelete("");
        notify({
          message: "Campaign deleted successfully",
          severity: NotificationSeverity.success,
        });
      })
      .catch((error) => {
        notify({
          message: error.message || "Error deleting campaign",
          severity: NotificationSeverity.error,
        });
      });
  };

  const refetchCampaigns = useCallback(async () => {
    if (organizationId && refetchOrgCampaigns) {
      await refetchOrgCampaigns({ orgId: organizationId });
    } else if (refetch) {
      await refetch();
    }
  }, [organizationId, refetchOrgCampaigns, refetch]);

  return {
    campaigns,
    loading,
    isOrgView,
    organizationId,
    error,
    deleteCampaignOpen,
    campaignIdToDelete,
    handleActionClick,
    setDeleteCampaignOpen,
    handleDelete,
    refetch: refetchCampaigns,
  };
};
